import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Home, ArrowRightLeft, AlertCircle } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * Landing half of the multi-property switcher.
 *
 * A person managing several properties picked another one from the switcher
 * in AdminLayout; that minted a single-use code on the property they were
 * ON and sent the browser here, on the property they're switching TO. This
 * page trades the code for a real session on this host and drops into the
 * dashboard — the tab is only ever here for a moment.
 *
 * Public route on purpose: the whole point is that there's no session here
 * yet. The code is what authenticates, and the server re-checks that the two
 * properties are linked in both directions before it will mint anything
 * (backend/routes/tenantProperties.js).
 */
export default function PropertySwitchPage() {
  const { hotelName, logoUrl } = useSettings();
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState("");

  // The code is single-use, so a second POST would always fail. React 18's
  // StrictMode double-invokes effects in development, which would otherwise
  // consume the code on the first run and show the error from the second.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const code = params.get("code");
    if (!code) { setError("This link is missing its switch code."); return; }

    (async () => {
      try {
        const res  = await fetch(`${API}/auth/switch-consume`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ code }),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error || "Could not switch property");
        login(json.token, json.features);
        // replace, so the back button doesn't return to a spent code.
        navigate("/admin/dashboard", { replace: true });
      } catch (err) {
        setError(err.message);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50
      flex items-center justify-center p-4">
      <div className="w-full max-w-sm text-center">
        {logoUrl ? (
          <img src={logoUrl} alt={hotelName}
            className="w-14 h-14 rounded-2xl object-cover mx-auto shadow-sm" />
        ) : (
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500
            flex items-center justify-center mx-auto shadow-sm">
            <Home size={26} className="text-white" />
          </div>
        )}

        {error ? (
          <div className="mt-6">
            <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center mx-auto">
              <AlertCircle size={20} className="text-rose-500" />
            </div>
            <p className="mt-3 font-semibold text-gray-900">Couldn't switch property</p>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <a href="/login"
              className="mt-5 inline-flex items-center justify-center min-h-[44px] px-5
                rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white
                text-sm font-semibold shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all">
              Sign in to {hotelName}
            </a>
          </div>
        ) : (
          <div className="mt-6">
            <ArrowRightLeft size={20} className="text-amber-500 mx-auto animate-pulse" />
            <p className="mt-3 font-semibold text-gray-900">Switching to {hotelName}…</p>
            <p className="mt-1 text-sm text-gray-500">One moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}
